import type React from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {newId} from '../../../domain/planText';
import type {ToolKey} from '../../../types/strategyTypes';
import type {PlanProps} from '../strategyProps';
import {EditableList} from '../EditableList';
import {PlanSection} from '../PlanSection';
import {EditIn, HelpTitle} from '../SectionLinks';

const BENEFITS_HELP = 'Savings and cost avoidance add up the PICK opportunities: savings in Implement and Challenge, and '
    + 'index-linked pricing unless it is in Kill. Move opportunities in PICK to change them.';

/**
 * Target benefits: savings, cost avoidance and value against influenceable spend (from PICK; gone while PICK is off),
 * plus non-financial targets.
 */
export const TargetsSection: React.FC<PlanProps & {openTool: (key: ToolKey) => void}> = ({plan, actions, money, openTool}) => {
    const t = plan.derived.targets;
    // Targets are plain strings on the server; give them stable ids for editing.
    const items = plan.content.targets.map((text, i) => ({id: `t${i}`, text}));
    const pickOff = plan.derived.toolsOff.includes('pick');
    const tiles = [
        {label: 'Financial savings', value: money(t.financial), hint: 'Implement and Challenge opportunities'},
        {label: 'Cost avoidance', value: money(t.avoidance), hint: 'Index-linked pricing'},
        {label: 'Value against influenceable spend', value: `${t.valuePct.toFixed(1)}%`,
            hint: t.valueLow ? 'Below the 20% breakthrough mark' : 'Breakthrough level (20% or more)', warn: t.valueLow},
    ];

    return (
        <PlanSection
            title={pickOff ? 'Target benefits'
                : <HelpTitle title="Target benefits" help={BENEFITS_HELP} links={[{label: 'PICK', onClick: () => openTool('pick')}]}/>}
            actions={!pickOff && <EditIn label="PICK" onClick={() => openTool('pick')}/>}
            edited={plan.edited.includes('targets')}>
            <Stack gap={2}>
                {!pickOff && <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)'}, gap: 1.5}}>
                    {tiles.map(tile => (
                        <Box key={tile.label} sx={{border: '1px solid', borderColor: tile.warn ? 'warning.main' : 'divider', borderRadius: 1, p: 1.5}}>
                            <Typography variant="caption" color="text.secondary">{tile.label}</Typography>
                            <Typography variant="h6" color={tile.warn ? 'warning.main' : 'text.primary'}>{tile.value}</Typography>
                            <Typography variant="caption" color="text.secondary">{tile.hint}</Typography>
                        </Box>
                    ))}
                </Box>}
                <Box>
                    <Typography variant="subtitle2" sx={{mb: 0.5}}>Non-financial targets</Typography>
                    <EditableList
                        items={items} fields={[{key: 'text', placeholder: 'Describe the target'}]}
                        readOnly={!plan.canEdit} addLabel="Add target" emptyText="No non-financial targets yet."
                        create={() => ({id: newId('t', items), text: 'New target'})}
                        onChange={(next, message) => void actions.save('targets', next.map(i => i.text), `Target ${message.toLowerCase()}`)}
                    />
                </Box>
            </Stack>
        </PlanSection>
    );
};
